// =====================================================================
// UX PREFS — central switch for the power-UX features
// =====================================================================
// Client-side only (localStorage). Deliberately NOT using the server
// prefs API → zero schema/migration risk, instant, works on any site
// the moment the app is installed. Mirrors the safe pattern already
// proven by form_actions.js.
//
// Keys:
//   density   : "comfortable" | "compact" | "wide"
//   focus     : 0 | 1
//   accountant: 0 | 1   (amount-in-words tooltip, quick copy, sel bar)
//   keyboard  : 0 | 1   (vim-leader nav, recents, favorites)
// =====================================================================

import { userKey } from "../utils/storage.js";

// Per-user key so density/focus/accountant choices stay isolated between
// different users signing into the SAME browser (Theme Lab user-scoping).
const LS_KEY = () => userKey("ux");
const DEFAULTS = {
	density: "comfortable",
	focus: 0,
	accountant: 1,
	keyboard: 1,
	pdf_export: 1,    // 📄 Export PDF button on every saved document
};
const VALID_DENSITY = ["comfortable", "compact", "wide"];

function read() {
	try {
		const raw = localStorage.getItem(LS_KEY());
		if (!raw) return { ...DEFAULTS };
		const p = JSON.parse(raw) || {};
		return {
			density: VALID_DENSITY.includes(p.density) ? p.density : DEFAULTS.density,
			focus: p.focus ? 1 : 0,
			accountant: p.accountant === 0 ? 0 : 1,
			keyboard: p.keyboard === 0 ? 0 : 1,
			pdf_export: p.pdf_export === 0 ? 0 : 1,
		};
	} catch (e) {
		return { ...DEFAULTS };
	}
}

function write(p) {
	try {
		localStorage.setItem(LS_KEY(), JSON.stringify(p));
	} catch (e) {}
}

function applyToDom(p) {
	const html = document.documentElement;
	try {
		html.setAttribute("data-e16-density", p.density);
		html.classList.toggle("e16-focus", !!p.focus);
		html.classList.toggle("e16-ux-accountant", !!p.accountant);
		html.classList.toggle("e16-ux-keyboard", !!p.keyboard);
		document.dispatchEvent(
			new CustomEvent("e16:ux-applied", { detail: { ...p } })
		);
	} catch (e) {}
}

export const uxPrefs = {
	DEFAULTS,
	VALID_DENSITY,

	get() {
		return read();
	},

	set(partial) {
		const next = { ...read(), ...partial };
		// sanitize
		if (!VALID_DENSITY.includes(next.density)) next.density = DEFAULTS.density;
		next.focus = next.focus ? 1 : 0;
		next.accountant = next.accountant ? 1 : 0;
		next.keyboard = next.keyboard ? 1 : 0;
		next.pdf_export = next.pdf_export ? 1 : 0;
		write(next);
		applyToDom(next);
		return next;
	},

	// Apply persisted prefs to the DOM (call at bootstrap, before paint).
	bootstrap() {
		applyToDom(read());
	},
};
